import React, { useEffect, useState } from 'react';
import { generateDiscountCode } from '../../shared/api/candyrack';
import { productOfferVariants } from '../../contexts/ProductsContext';
import { usePopupContext } from '../../contexts/Settings';
import { getCurrency } from '../../shared/product-page-placement/currency';
import { getDiscountedPrice } from '../../dissolve/helpers';
import { formatMoney } from '../../dissolve/utils';
import {
  AddButtonAction,
  ButtonAction,
  CandyRackSettings,
  Offer,
  popupLayoutCssPostfix,
  Product,
  ProductVariant,
} from '../../types';
import { saveDiscountCodeForCandyrack } from '../../shared/utils/discounts';
import {
  AddToCartButton,
  Select,
  SkeletonOffer,
  UpgradeButton,
} from '../elements';
import { BACKEND_MAX_VALUE } from '../elements/OfferQuantity';
import { ProductOfferView } from './ProductOfferView';
import { useCandyRackContext } from '../../contexts/CandyRackContext';
import { REGISTERED_APPS } from '../../shared/types/Apps';
import { logInfo } from '../../shared/utils/logging';

const ProductOfferItem = ({
  product,
  offer,
  resetSelectedProduct,
  handleAddToCart,
  handleRemoveFromCart,
  indexInOffers,
  offerCount,
  parentProduct,
  onClose,
  storefrontAccessToken,
}: {
  product: Product;
  offer: Offer;
  resetSelectedProduct: () => void;
  handleAddToCart: (
    offer: Offer,
    product?: Product,
    action?: AddButtonAction,
  ) => Promise<boolean>;
  handleRemoveFromCart: (offer: Offer, variant?: number) => Promise<void>;
  indexInOffers: number | undefined;
  offerCount: number;
  parentProduct: Product;
  onClose: (
    ignoreUpgrade: boolean,
    closeAction?: ButtonAction,
  ) => Promise<void>;
  storefrontAccessToken: string | undefined;
}) => {
  const {
    upgradeOffer,
    quantity: parentQuantity,
    parentProductVariantId,
  } = useCandyRackContext();

  const startingQuantity = offer.match_product_quantity
    ? Math.min(BACKEND_MAX_VALUE, parentQuantity)
    : 1;

  const [added, setAdded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [quantity, setQuantity] = useState(startingQuantity);
  const [offerNote, setOfferNote] = useState<string>();
  const [variant, setVariant] = useState<ProductVariant>();
  const filteredProductVariantsFromOffer = productOfferVariants(product, offer);

  const upgradeOfferId = upgradeOffer?.id;
  const AcceptOfferButton = offer.is_upgrade ? UpgradeButton : AddToCartButton;

  useEffect(() => {
    const availableVariants = productOfferVariants(product, offer).filter(
      (v) => v.available,
    );

    const defaultVariant = availableVariants[0];

    setVariant(
      offer.match_parent_product_variant && offer.is_same_product_offer
        ? availableVariants.find(
            (variant) => variant.id === parentProductVariantId,
          ) || defaultVariant
        : defaultVariant,
    );
  }, [product, offer, parentProductVariantId]);

  useEffect(() => {
    if (offer.is_upgrade && upgradeOfferId !== offer.id) {
      setAdded(false);
    }
  }, [upgradeOfferId, offer]);

  const candyRackSettings = usePopupContext().settings as CandyRackSettings;

  const handleOfferVariantChange = (
    event: React.ChangeEvent<HTMLSelectElement>,
  ) => {
    setVariant(
      product.variants.find((v) => v.id === Number(event.target.value)),
    );
  };

  const handleRemoveFromCartClick = async () => {
    setAdded(false);
    setLoading(true);
    if (!variant || !product) {
      return;
    }

    offer.shopifyProductVariantIdComputedInFE = variant.id;
    offer.shopifyProductHandleComputedInFE = product.handle;

    await handleRemoveFromCart(offer);
    setLoading(false);
  };

  const handleAddToCartClick = async () => {
    setLoading(true);

    if (!variant || !product) {
      return;
    }

    resetSelectedProduct();

    offer.shopifyProductVariantIdComputedInFE = variant.id;
    offer.shopifyProductHandleComputedInFE = product.handle;
    offer.quantityComputedInFE = quantity;
    offer.noteComputedInFE = offerNote || '';
    product.discountedPrice = getDiscountedPrice(
      offer,
      variant,
      quantity,
      REGISTERED_APPS.ADD_TO_CART,
    );

    // variant.id is used to show correct image when upgrading
    const productAdded = await handleAddToCart(
      offer,
      product,
      candyRackSettings.buttons.add.action,
    );

    setAdded(productAdded);

    if (offer.apply_discount) {
      const discountedPrice = getDiscountedPrice(offer, variant, quantity);
      let discountCode = offer.discount_code;
      if (
        (offer.use_recommended_products || offer.is_same_product_offer) &&
        !discountCode
      ) {
        discountCode = await generateDiscountCode(
          window.Shopify.shop,
          offer.id,
          product.id,
          offer.quantityComputedInFE || 1,
        );
      }
      await saveDiscountCodeForCandyrack(
        discountCode,
        variant.id,
        discountedPrice,
        getDiscount(),
        offer.is_upgrade ? product.id : parentProduct.id,
        true,
        storefrontAccessToken,
      );
    }
    if (
      !candyRackSettings.buttons.add.action ||
      candyRackSettings.buttons.add.action === 'keep_popup_open'
    ) {
      logInfo(
        `Action: ${candyRackSettings.buttons.add.action}, nothing more to be done.`,
        REGISTERED_APPS.ADD_TO_CART,
      );
    } else {
      logInfo(
        `Action: ${candyRackSettings.buttons.add.action}, proceeding with handleClose.`,
        REGISTERED_APPS.ADD_TO_CART,
      );
      void onClose(false, candyRackSettings.buttons.add.action);
    }
    setLoading(false);
  };

  const getDiscount = () => {
    const { amount_type, amount } = offer;

    if (amount_type === 'fixed_amount') {
      return formatMoney(Number(amount) * quantity, getCurrency().currency);
    }

    return `${new Intl.NumberFormat(undefined, {
      maximumFractionDigits: 2,
    }).format(Number(amount))}%`;
  };

  const isProductUnavailable =
    product && !product.loading && (!product.available || !variant);

  if (isProductUnavailable) {
    return null;
  }

  if (!product || !variant) {
    return (
      <SkeletonOffer indexInOffers={indexInOffers} offerCount={offerCount} />
    );
  }

  const select = (className: string) =>
    filteredProductVariantsFromOffer.length > 1 ? (
      <Select
        className={className}
        disabled={added}
        onChange={handleOfferVariantChange}
        value={variant.id}
      >
        {filteredProductVariantsFromOffer.map((v) => (
          <option key={v.id} value={v.id} disabled={!v.available}>
            {v.title}
          </option>
        ))}
      </Select>
    ) : null;

  const popupLayoutCssPostfixValue = popupLayoutCssPostfix(candyRackSettings);

  const upgradeText =
    upgradeOfferId === offer.id && added
      ? candyRackSettings.text.upgraded
      : candyRackSettings.text.upgrade;

  const addText = added
    ? candyRackSettings.text.added
    : candyRackSettings.text.add;

  let primaryButton = (
    <AcceptOfferButton
      disabled={added || loading}
      onClick={handleAddToCartClick}
      data-cy="add"
      data-accepted-product-variant={added ? variant.id : undefined}
      className={`Button-Add${popupLayoutCssPostfixValue} ${
        added ? `Button-Add-Added${popupLayoutCssPostfixValue}` : ''
      }`}
    >
      {addText}
    </AcceptOfferButton>
  );

  if (offer.is_upgrade) {
    primaryButton = (
      <AcceptOfferButton
        disabled={added || loading}
        onClick={handleAddToCartClick}
        data-cy="upgrade"
        className={`Button-Add${popupLayoutCssPostfixValue} Button-Upgrade${popupLayoutCssPostfixValue} ${
          added
            ? `Button-Add-Added${popupLayoutCssPostfixValue} Button-Upgrade-Upgraded${popupLayoutCssPostfixValue}`
            : ''
        }`}
      >
        {upgradeText}
      </AcceptOfferButton>
    );
  }

  return (
    <ProductOfferView
      added={added}
      handleOfferQuantityChange={setQuantity}
      handleRemoveFromCartClick={handleRemoveFromCartClick}
      loading={loading}
      offer={offer}
      offerCount={offerCount}
      indexInOffers={indexInOffers}
      product={product}
      quantity={quantity}
      primaryButton={primaryButton}
      select={select}
      setOfferNote={setOfferNote}
      variant={variant}
      offerNote={offerNote}
      filteredProductVariantsFromOffer={filteredProductVariantsFromOffer}
    />
  );
};

export default ProductOfferItem;
